import React, { useState, useEffect, useCallback } from 'react';
import API from 'shared/api';
import { FiPlus, FiEdit2, FiTrash2, FiX, FiSave, FiSearch } from 'react-icons/fi';
import { C } from 'shared/darkColors';

const EMPTY_FORM = {
  cedula: '',
  nombre: '',
  usuario: '',
  clave: '',
  correo: '',
  idempresa: '',
  codigo_sede: '',
  rol: 'asesor',
};

const ROL_COLORS = {
  admin: '#ef4444',
  supervisor: '#eab308',
  asesor: '#3b82f6',
  delivery: '#22c55e',
};
const ROL_LABELS = {
  admin: 'Admin',
  supervisor: 'Supervisor',
  asesor: 'Asesor',
  delivery: 'Delivery',
};

export default function Usuarios() {
  const [usuarios, setUsuarios] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [toast, setToast] = useState(null);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const showToast = (msg, ok = true) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 2500);
  };

  const fetchUsuarios = useCallback(async () => {
    try {
      const r = await API.get('/usuarios/listar');
      if (r.data.status === 'success') setUsuarios(r.data.data);
    } catch {
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchEmpresas = useCallback(async () => {
    try {
      const r = await API.get('/super/empresas');
      if (r.data.status === 'success') setEmpresas(r.data.data.filter((e) => e.activa !== false));
    } catch {}
  }, []);

  useEffect(() => {
    fetchUsuarios();
    fetchEmpresas();
  }, [fetchUsuarios, fetchEmpresas]);

  const openModal = (u = null) => {
    if (u) {
      setForm({
        cedula: u.cedula || '',
        nombre: u.nombre || '',
        usuario: u.usuario || '',
        clave: '',
        correo: u.correo || '',
        idempresa: u.idempresa || '',
        codigo_sede: u.codigo_sede || '',
        rol: u.rol || 'asesor',
      });
      setModal('edit');
    } else {
      setForm(EMPTY_FORM);
      setModal('new');
    }
  };

  const save = async () => {
    if (!form.cedula || !form.nombre || !form.usuario)
      return showToast('Cedula, nombre y usuario requeridos', false);
    setSaving(true);
    try {
      const params = new URLSearchParams();
      params.append('nombre', form.nombre);
      params.append('usuario', form.usuario);
      if (form.clave) params.append('clave', form.clave);
      params.append('correo', form.correo || '');
      if (form.idempresa) params.append('idempresa', form.idempresa);
      if (form.codigo_sede) params.append('codigo_sede', form.codigo_sede);
      params.append('rol', form.rol);
      if (modal === 'new') {
        if (!form.idempresa) return showToast('Debe seleccionar una empresa', false);
        params.append('cedula', form.cedula);
        params.append('idempresa', form.idempresa);
        await API.post(`/usuarios/agregar?${params.toString()}`);
        showToast('Usuario creado');
      } else {
        await API.put(`/usuarios/modificar/${form.cedula}?${params.toString()}`);
        showToast('Usuario actualizado');
      }
      setModal(null);
      fetchUsuarios();
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.response?.data?.message || 'Error al guardar';
      showToast(msg, false);
    } finally {
      setSaving(false);
    }
  };

  const deleteUser = async (cedula) => {
    if (!window.confirm('Eliminar este usuario?')) return;
    try {
      await API.delete(`/usuarios/eliminar/${cedula}`);
      showToast('Usuario eliminado');
      fetchUsuarios();
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.response?.data?.message || 'Error al eliminar';
      showToast(msg, false);
    }
  };

  const getEmpresaNombre = (idempresa) => {
    if (!idempresa) return '--';
    const e = empresas.find((emp) => emp.id === idempresa);
    return e ? e.nombre : `ID ${idempresa}`;
  };

  const filtered = usuarios.filter(
    (u) =>
      !search ||
      u.nombre?.toLowerCase().includes(search.toLowerCase()) ||
      u.usuario?.toLowerCase().includes(search.toLowerCase()) ||
      u.cedula?.toLowerCase().includes(search.toLowerCase()) ||
      u.correo?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return <div style={C.loading}>Cargando...</div>;

  return (
    <div className="sa-usr-wrapper" style={C.wrapper}>
      <style>{`@keyframes toastIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        @media (max-width: 768px) {
          .sa-usr-wrapper { padding: 16px 12px !important; }
          .sa-usr-section { padding: 18px !important; border-radius: 12px !important; }
          .sa-usr-section-head { flex-direction: column; gap: 12px; align-items: flex-start; }
          .sa-usr-search-wrap { width: 100% !important; }
          .sa-usr-table-wrap { overflow-x: auto; -webkit-overflow-scrolling: touch; }
          .sa-usr-table-wrap table { min-width: 700px; }
          .sa-usr-modal-box { max-width: 95vw !important; padding: 24px !important; max-height: 90vh !important; border-radius: 16px !important; }
          .sa-usr-form-row { grid-template-columns: 1fr !important; }
        }
      `}</style>
      <div style={C.topBar}>
        <div>
          <div style={C.title}>Usuarios</div>
          <div style={C.subtitle}>Todos los usuarios de la plataforma</div>
        </div>
      </div>

      <div className="sa-usr-section" style={C.section}>
        <div className="sa-usr-section-head" style={C.sectionHead}>
          <div style={C.sectionTitle}>Usuarios ({filtered.length})</div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <div className="sa-usr-search-wrap" style={C.searchWrap}>
              <FiSearch size={14} color="#9ca3af" />
              <input
                placeholder="Buscar usuarios..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={C.searchInput}
              />
            </div>
            <button onClick={() => openModal()} style={C.btnPrimary}>
              <FiPlus size={14} /> Nuevo
            </button>
          </div>
        </div>
        <div className="sa-usr-table-wrap">
          <table style={C.table}>
            <thead>
              <tr>
                <th style={C.th}>Cedula</th>
                <th style={C.th}>Nombre</th>
                <th style={C.th}>Usuario</th>
                <th style={C.th}>Correo</th>
                <th style={C.th}>Empresa</th>
                <th style={C.th}>Rol</th>
                <th style={C.th}>Super Admin</th>
                <th style={C.th}></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={8} style={C.emptyRow}>
                    Sin resultados
                  </td>
                </tr>
              ) : (
                filtered.map((u) => {
                  const color = ROL_COLORS[u.rol] || '#6b7280';
                  return (
                    <tr key={u.cedula}>
                      <td style={{ ...C.td, fontFamily: 'monospace', fontSize: '12px' }}>
                        {u.cedula}
                      </td>
                      <td style={{ ...C.td, fontWeight: 600 }}>{u.nombre}</td>
                      <td style={C.td}>{u.usuario}</td>
                      <td style={C.td}>{u.correo || '--'}</td>
                      <td style={C.td}>{getEmpresaNombre(u.idempresa)}</td>
                      <td style={C.td}>
                        <span style={C.badge(color + '18', color)}>
                          {ROL_LABELS[u.rol] || u.rol}
                        </span>
                      </td>
                      <td style={C.td}>
                        {u.es_super_admin ? (
                          <span style={C.badge('#fef2f2', '#dc2626')}>Si</span>
                        ) : (
                          '--'
                        )}
                      </td>
                      <td style={C.td}>
                        <div style={{ display: 'flex', gap: '6px' }}>
                          <button
                            onClick={() => openModal(u)}
                            style={{ ...C.btnSm, background: '#eff6ff', color: '#2563eb' }}
                          >
                            <FiEdit2 size={12} />
                          </button>
                          <button onClick={() => deleteUser(u.cedula)} style={C.btnDanger}>
                            <FiTrash2 size={12} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {modal && (
        <div style={C.modal} onClick={() => setModal(null)}>
          <div className="sa-usr-modal-box" style={C.modalBox} onClick={(e) => e.stopPropagation()}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '24px',
              }}
            >
              <div style={C.modalTitle}>
                {modal === 'edit' ? 'Editar Usuario' : 'Nuevo Usuario'}
              </div>
              <button
                onClick={() => setModal(null)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#9ca3af',
                  padding: '4px',
                  borderRadius: '8px',
                }}
              >
                <FiX size={20} />
              </button>
            </div>
            <div className="sa-usr-form-row" style={C.formRow}>
              <div style={C.formGroup}>
                <label style={C.formLabel}>Cedula *</label>
                <input
                  value={form.cedula}
                  onChange={(e) => setForm({ ...form, cedula: e.target.value })}
                  disabled={modal === 'edit'}
                  style={{ ...C.formInput, background: modal === 'edit' ? '#f9fafb' : '#fff' }}
                />
              </div>
              <div style={C.formGroup}>
                <label style={C.formLabel}>Nombre *</label>
                <input
                  value={form.nombre}
                  onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                  style={C.formInput}
                />
              </div>
            </div>
            <div className="sa-usr-form-row" style={C.formRow}>
              <div style={C.formGroup}>
                <label style={C.formLabel}>Usuario *</label>
                <input
                  value={form.usuario}
                  onChange={(e) => setForm({ ...form, usuario: e.target.value })}
                  style={C.formInput}
                />
              </div>
              <div style={C.formGroup}>
                <label style={C.formLabel}>Clave {modal === 'edit' ? '(dejar vacio)' : '*'}</label>
                <input
                  type="password"
                  value={form.clave}
                  onChange={(e) => setForm({ ...form, clave: e.target.value })}
                  style={C.formInput}
                  placeholder={modal === 'edit' ? 'Sin cambios...' : ''}
                />
              </div>
            </div>
            <div style={C.formGroup}>
              <label style={C.formLabel}>Correo</label>
              <input
                value={form.correo}
                onChange={(e) => setForm({ ...form, correo: e.target.value })}
                style={C.formInput}
              />
            </div>
            <div className="sa-usr-form-row" style={C.formRow}>
              <div style={C.formGroup}>
                <label style={C.formLabel}>Empresa</label>
                <select
                  value={form.idempresa}
                  onChange={(e) => setForm({ ...form, idempresa: e.target.value })}
                  style={C.formSelect}
                >
                  <option value="">Sin empresa (super admin)</option>
                  {empresas.map((e) => (
                    <option key={e.id} value={e.id}>
                      {e.nombre}
                    </option>
                  ))}
                </select>
              </div>
              <div style={C.formGroup}>
                <label style={C.formLabel}>Rol</label>
                <select
                  value={form.rol}
                  onChange={(e) => setForm({ ...form, rol: e.target.value })}
                  style={C.formSelect}
                >
                  <option value="admin">Admin</option>
                  <option value="supervisor">Supervisor</option>
                  <option value="asesor">Asesor</option>
                  <option value="delivery">Delivery</option>
                  <option value="armador">Armador</option>
                </select>
              </div>
            </div>
            <div style={C.formGroup}>
              <label style={C.formLabel}>Codigo Sede</label>
              <input
                value={form.codigo_sede}
                onChange={(e) => setForm({ ...form, codigo_sede: e.target.value })}
                style={C.formInput}
                placeholder="Ej: E10-1"
              />
            </div>
            <div style={C.formActions}>
              <button onClick={() => setModal(null)} style={C.btnOutline}>
                Cancelar
              </button>
              <button
                onClick={save}
                disabled={saving}
                style={{ ...C.btnPrimary, opacity: saving ? 0.6 : 1 }}
              >
                <FiSave size={14} /> Guardar
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div style={{ ...C.toast, background: toast.ok ? '#111827' : '#dc2626' }}>{toast.msg}</div>
      )}
    </div>
  );
}
